import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus, Trash2, FileDown, ArrowRight, X, Edit3, ClipboardList, PackageCheck,
  CheckCircle2, XCircle, Clock, Loader2, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCompany } from "@/lib/company";
import { useAuth } from "@/lib/auth";

const fmtMoney = (v, cur = "INR") => {
  const sym = { INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "د.إ" }[cur] || (cur + " ");
  return `${sym}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

function StatusChip({ status }) {
  const map = {
    draft: ["chip-neutral", Clock],
    submitted: ["chip-info", ArrowRight],
    approved: ["chip-success", CheckCircle2],
    rejected: ["chip-danger", XCircle],
    pending_approval: ["chip-warn", ShieldAlert],
    confirmed: ["chip-success", CheckCircle2],
    received: ["chip-info", PackageCheck],
    invoiced: ["chip-info", ArrowRight],
    cancelled: ["chip-danger", XCircle],
    converted: ["chip-info", ArrowRight],
  };
  const [cls, Icon] = map[status] || ["chip-neutral", Clock];
  return <span className={`chip ${cls} whitespace-nowrap`}><Icon size={11}/>{status.replace("_"," ")}</span>;
}

function PurchaseModal({ open, kind, initial, onClose, onSaved }) {
  const { active } = useCompany();
  const cur = active?.base_currency || "INR";
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    party_id: "", doc_date: new Date().toISOString().slice(0, 10),
    expected_receipt: new Date(Date.now() + 14 * 864e5).toISOString().slice(0, 10),
    currency: cur, exchange_rate: 1, notes: "", reference: "",
    lines: [], status: "draft",
  });

  useEffect(() => {
    if (!open) return;
    Promise.all([api.get("/parties?type=vendor"), api.get("/items")])
      .then(([p, i]) => { setParties(p.data.parties || []); setItems(i.data.items || []); })
      .catch(() => {});
    if (initial) {
      setF({
        party_id: initial.party_id || "",
        doc_date: initial.doc_date || new Date().toISOString().slice(0, 10),
        expected_receipt: initial.expected_receipt || "",
        currency: initial.currency || cur, exchange_rate: initial.exchange_rate || 1,
        notes: initial.notes || "", reference: initial.reference || "",
        lines: (initial.lines || []).map((l) => ({
          item_id: l.item_id || "", name: l.name, hsn: l.hsn || "",
          qty: l.qty, unit_price: l.unit_price,
          discount_pct: l.discount_pct || 0, tax_rate: l.tax_rate || 0,
        })),
        status: initial.status || "draft",
      });
    } else {
      setF((s) => ({ ...s, party_id: "", lines: [], status: "draft", notes: "" }));
    }
  }, [open, initial, cur]);

  const totals = useMemo(() => {
    let sub = 0, tax = 0;
    f.lines.forEach((l) => {
      const qty = Number(l.qty || 0), price = Number(l.unit_price || 0);
      const disc = Number(l.discount_pct || 0), rate = Number(l.tax_rate || 0);
      const taxable = qty * price * (1 - disc / 100);
      sub += taxable; tax += taxable * (rate / 100);
    });
    return { sub, tax, grand: sub + tax };
  }, [f.lines]);

  const addLine = () => setF((s) => ({ ...s, lines: [...s.lines, { item_id: "", name: "", qty: 1, unit_price: 0, tax_rate: 18, discount_pct: 0, hsn: "" }] }));
  const updLine = (i, patch) => setF((s) => ({ ...s, lines: s.lines.map((l, j) => j === i ? { ...l, ...patch } : l) }));
  const rmLine = (i) => setF((s) => ({ ...s, lines: s.lines.filter((_, j) => j !== i) }));
  const onItemPick = (i, itemId) => {
    const it = items.find((x) => x.id === itemId);
    if (!it) return updLine(i, { item_id: "" });
    updLine(i, { item_id: it.id, name: it.name, hsn: it.hsn || "",
      unit_price: it.purchase_price || it.sales_price || 0, tax_rate: it.tax_rate ?? 18 });
  };

  const submit = async () => {
    if (kind !== "purchase_request" && !f.party_id) return toast.error("Select a vendor");
    if (f.lines.length === 0) return toast.error("Add at least one line");
    setSaving(true);
    try {
      const body = {
        kind, party_id: f.party_id || null,
        doc_date: f.doc_date, expected_receipt: f.expected_receipt || null,
        currency: f.currency, exchange_rate: Number(f.exchange_rate || 1),
        notes: f.notes || null, reference: f.reference || null,
        status: f.status,
        lines: f.lines.map((l) => ({
          item_id: l.item_id || null, name: l.name, hsn: l.hsn || null,
          qty: Number(l.qty || 0), unit_price: Number(l.unit_price || 0),
          tax_rate: Number(l.tax_rate || 0), discount_pct: Number(l.discount_pct || 0),
        })),
      };
      const url = initial ? `/purchase-docs/${initial.id}` : `/purchase-docs`;
      const method = initial ? "put" : "post";
      const { data } = await api[method](url, body);
      if (data.status === "pending_approval") {
        toast.info("Above threshold — sent for Super Admin approval");
      } else {
        toast.success(initial ? "Saved" : "Created");
      }
      onSaved?.(data);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!open) return null;
  const labels = {
    purchase_request: "Purchase Request", purchase_order: "Purchase Order", grn: "Goods Receipt Note",
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-4xl p-4 sm:p-6 my-4 sm:my-0 sm:max-h-[92vh] sm:overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="pd-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline mb-1">{labels[kind]}</div>
            <h2 className="text-lg sm:text-xl font-bold text-[#0F172A]">
              {initial ? `Edit ${initial.number}` : `New ${labels[kind]}`}
            </h2>
          </div>
          <button onClick={onClose} className="p-2" style={{ minWidth: 44, minHeight: 44 }}><X size={18}/></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div>
            <label className="label-field">Vendor {kind !== "purchase_request" && "*"}</label>
            <select data-testid="pd-party" className="input-clean" value={f.party_id} onChange={(e) => setF({ ...f, party_id: e.target.value })}>
              <option value="">{kind === "purchase_request" ? "TBD / Any vendor" : "Select vendor"}</option>
              {parties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label-field">Date</label>
            <input type="date" className="input-clean" value={f.doc_date} onChange={(e) => setF({ ...f, doc_date: e.target.value })}/>
          </div>
          <div>
            <label className="label-field">Expected Receipt</label>
            <input type="date" className="input-clean" value={f.expected_receipt} onChange={(e) => setF({ ...f, expected_receipt: e.target.value })}/>
          </div>
          <div>
            <label className="label-field">Reference</label>
            <input className="input-clean" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })}/>
          </div>
        </div>

        <div className="mb-2 flex items-center justify-between">
          <label className="label-field">Lines</label>
          <button onClick={addLine} className="btn-secondary py-1 px-2 text-xs" data-testid="pd-add-line"><Plus size={12}/> Add Line</button>
        </div>
        {f.lines.map((l, i) => (
          <div key={i} className="grid grid-cols-2 sm:grid-cols-12 gap-2 mb-3 pb-3 border-b sm:border-none border-[#E2E8F0]">
            <div className="col-span-2 sm:col-span-4">
              <select className="input-clean" value={l.item_id} onChange={(e) => onItemPick(i, e.target.value)}>
                <option value="">-- Custom line --</option>
                {items.map((it) => <option key={it.id} value={it.id}>{it.name}</option>)}
              </select>
              <input className="input-clean mt-1" placeholder="Description" value={l.name} onChange={(e) => updLine(i, { name: e.target.value })}/>
            </div>
            <div className="sm:col-span-1"><input type="number" inputMode="decimal" className="input-clean" placeholder="Qty" value={l.qty} onChange={(e) => updLine(i, { qty: e.target.value })}/></div>
            <div className="sm:col-span-2"><input type="number" inputMode="decimal" className="input-clean" placeholder="Rate" value={l.unit_price} onChange={(e) => updLine(i, { unit_price: e.target.value })}/></div>
            <div className="sm:col-span-2"><input type="number" inputMode="decimal" className="input-clean" placeholder="Disc %" value={l.discount_pct} onChange={(e) => updLine(i, { discount_pct: e.target.value })}/></div>
            <div className="sm:col-span-2"><input type="number" inputMode="decimal" className="input-clean" placeholder="Tax %" value={l.tax_rate} onChange={(e) => updLine(i, { tax_rate: e.target.value })}/></div>
            <div className="col-span-2 sm:col-span-1 flex items-center justify-end"><button onClick={() => rmLine(i)} className="text-[#DC2626] p-2"><Trash2 size={14}/></button></div>
          </div>
        ))}

        <div className="mt-4 flex justify-end">
          <div className="w-full sm:w-72 space-y-1 text-sm font-mono-data">
            <div className="flex justify-between text-[#64748B]"><span>Subtotal</span><span>{fmtMoney(totals.sub, f.currency)}</span></div>
            <div className="flex justify-between text-[#64748B]"><span>Tax</span><span>{fmtMoney(totals.tax, f.currency)}</span></div>
            <div className="flex justify-between font-bold text-[#0F172A] pt-1 border-t border-[#E2E8F0]"><span>Grand Total</span><span data-testid="pd-grand">{fmtMoney(totals.grand, f.currency)}</span></div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <div>
            <label className="label-field">Status</label>
            <select className="input-clean" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
              {kind === "purchase_request" && <><option value="draft">Draft</option><option value="submitted">Submitted</option></>}
              {kind === "purchase_order" && <><option value="draft">Draft</option><option value="confirmed">Confirmed</option></>}
              {kind === "grn" && <><option value="draft">Draft</option><option value="received">Received</option></>}
            </select>
          </div>
          <div>
            <label className="label-field">Notes</label>
            <textarea rows={2} className="input-clean" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}/>
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn-ghost w-full sm:w-auto justify-center">Cancel</button>
          <button data-testid="pd-save" onClick={submit} disabled={saving} className="btn-primary w-full sm:w-auto justify-center">
            {saving ? "Saving…" : (initial ? "Save Changes" : `Create ${labels[kind]}`)}
          </button>
        </div>
      </div>
    </div>
  );
}


export function PurchaseCyclePage({ kind }) {
  const { active } = useCompany();
  const { role } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [converting, setConverting] = useState(null);
  const canWrite = ["owner", "admin", "sales", "super_admin"].includes(role);
  const cur = active?.base_currency || "INR";
  const labels = {
    purchase_request: { title: "Purchase Requests", subtitle: "Internal requests for procurement. Convert accepted requests to Purchase Orders.", one: "Purchase Request", short: "PR" },
    purchase_order: { title: "Purchase Orders", subtitle: "Confirmed orders to your vendors. Convert to a purchase bill when goods arrive.", one: "Purchase Order", short: "PO" },
    grn: { title: "Goods Receipt Notes", subtitle: "Log every consignment received. Reference against the source PO.", one: "GRN", short: "GRN" },
  }[kind];

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/purchase-docs?kind=${kind}`);
      setRows(data.items || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (active) load(); }, [active, kind]); // eslint-disable-line

  const downloadPdf = async (row) => {
    try {
      const res = await api.get(`/purchase-docs/${row.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(row.number || "doc").replace(/\//g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "PDF failed"); }
  };
  const convert = async (row) => {
    setConverting(row.id);
    try {
      const endpoint = kind === "purchase_request" ? "convert-to-po" : "convert-to-bill";
      const { data } = await api.post(`/purchase-docs/${row.id}/${endpoint}`);
      toast.success(kind === "purchase_request"
        ? `PO ${data.purchase_order.number} created${data.purchase_order.approval_status === "pending" ? " (pending approval)" : ""}`
        : `Bill ${data.invoice.number} created`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Conversion failed"); }
    finally { setConverting(null); }
  };
  const del = async (row) => {
    if (!window.confirm(`Delete ${row.number}?`)) return;
    try { await api.delete(`/purchase-docs/${row.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid={`${kind}-page`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="overline mb-1">Purchase Cycle</div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A] flex items-center gap-2">
            <ClipboardList className="text-[#FF5500]" size={24}/> {labels.title}
          </h1>
          <div className="text-sm text-[#64748B] mt-1">{labels.subtitle}</div>
        </div>
        {canWrite && (
          <button className="btn-primary w-full sm:w-auto justify-center" onClick={() => { setEditing(null); setModalOpen(true); }} data-testid={`new-${kind}-btn`}>
            <Plus size={14}/> New {labels.short}
          </button>
        )}
      </div>

      <div className="card-surface p-3 sm:p-4">
        {loading ? <div className="py-6 text-center text-[#64748B]">Loading…</div>
         : rows.length === 0 ? <div className="py-8 text-center text-[#64748B]" data-testid={`${kind}-empty`}>No {labels.title.toLowerCase()} yet.</div>
         : (<>
          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {rows.map((r) => (
              <div key={r.id} className="border border-[#E2E8F0] rounded-lg p-3 bg-white" data-testid={`pd-card-${r.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="font-mono-data font-semibold text-[#0F172A] text-sm">{r.number}</div>
                    <div className="text-sm truncate">{r.party_snapshot?.name || "—"}</div>
                    <div className="text-[11px] text-[#64748B] font-mono-data mt-0.5">{r.doc_date}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono-data font-bold text-[#0F172A]">{fmtMoney(r.grand_total, r.currency || cur)}</div>
                    <div className="mt-1"><StatusChip status={r.status}/></div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 items-center pt-2 border-t border-[#F1F5F9]">
                  {canWrite && !["converted", "invoiced", "cancelled", "pending_approval"].includes(r.status) && (
                    <button onClick={() => { setEditing(r); setModalOpen(true); }} className="text-[#64748B] p-1"><Edit3 size={16}/></button>
                  )}
                  <button onClick={() => downloadPdf(r)} className="text-[#0F172A] p-1"><FileDown size={16}/></button>
                  {canWrite && kind !== "grn" && !["converted", "invoiced", "cancelled", "pending_approval", "draft"].includes(r.status) && (
                    <button onClick={() => convert(r)} disabled={converting === r.id} className="text-[#FF5500] p-1 flex items-center gap-1 text-xs font-semibold" data-testid={`pd-convert-m-${r.id}`}>
                      {converting === r.id ? <Loader2 size={14} className="animate-spin"/> : <ArrowRight size={14}/>}
                      {kind === "purchase_request" ? "→ PO" : "→ Bill"}
                    </button>
                  )}
                  {r.converted_to_bill_id && (
                    <Link to={`/app/invoices/${r.converted_to_bill_id}`} className="text-[#2563EB] text-[11px] font-semibold">View Bill</Link>
                  )}
                  {canWrite && r.status === "draft" && (
                    <button onClick={() => del(r)} className="text-[#DC2626] p-1 ml-auto"><Trash2 size={16}/></button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {/* Desktop table */}
          <table className="table-clean hidden md:table">
            <thead><tr><th>Number</th><th>Date</th><th>Vendor</th><th>Expected</th><th className="text-right">Total</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`pd-row-${r.id}`}>
                  <td className="font-mono-data text-xs font-semibold">{r.number}</td>
                  <td className="font-mono-data text-xs">{r.doc_date}</td>
                  <td>{r.party_snapshot?.name || "—"}</td>
                  <td className="font-mono-data text-xs">{r.expected_receipt || "—"}</td>
                  <td className="text-right font-mono-data font-semibold">{fmtMoney(r.grand_total, r.currency || cur)}</td>
                  <td><StatusChip status={r.status}/></td>
                  <td>
                    <div className="flex gap-3 justify-end items-center">
                      {canWrite && !["converted", "invoiced", "cancelled", "pending_approval"].includes(r.status) && <button onClick={() => { setEditing(r); setModalOpen(true); }} className="text-[#64748B] hover:text-[#FF5500]"><Edit3 size={14}/></button>}
                      <button onClick={() => downloadPdf(r)} className="text-[#0F172A] hover:text-[#FF5500]" data-testid={`pd-pdf-${r.id}`}><FileDown size={14}/></button>
                      {canWrite && kind !== "grn" && !["converted", "invoiced", "cancelled", "pending_approval", "draft"].includes(r.status) && (
                        <button onClick={() => convert(r)} disabled={converting === r.id} className="text-[#FF5500] hover:text-[#0F172A] flex items-center gap-1 text-xs font-semibold" data-testid={`pd-convert-${r.id}`}>
                          {converting === r.id ? <Loader2 size={12} className="animate-spin"/> : <ArrowRight size={12}/>}
                          {kind === "purchase_request" ? "→ PO" : "→ Bill"}
                        </button>
                      )}
                      {r.converted_to_bill_id && <Link to={`/app/invoices/${r.converted_to_bill_id}`} className="text-[#2563EB] text-xs font-semibold">Bill</Link>}
                      {canWrite && r.status === "draft" && <button onClick={() => del(r)} className="text-[#DC2626] hover:text-[#0F172A]"><Trash2 size={14}/></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>)}
      </div>

      <PurchaseModal open={modalOpen} kind={kind} initial={editing} onClose={() => setModalOpen(false)} onSaved={load}/>
    </div>
  );
}

export function PurchaseRequests() { return <PurchaseCyclePage kind="purchase_request"/>; }
export function PurchaseOrders() { return <PurchaseCyclePage kind="purchase_order"/>; }
export function GRNs() { return <PurchaseCyclePage kind="grn"/>; }
export default PurchaseOrders;
